import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { safeParseDate } from "@/utils/dateValidation";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatEuro(value: string | null | number | undefined, includeCurrencySymbol: boolean = true): string {
  if (value === null || value === undefined) return "N/A";
  
  // Convert string to number if needed
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return "N/A";
  
  const formattedValue = num >= 1e9
    ? `${(num / 1e9).toFixed(1)}B`
    : num >= 1e6
    ? `${(num / 1e6).toFixed(1)}M`
    : num >= 1e3
    ? `${(num / 1e3).toFixed(1)}K`
    : num.toFixed(0);
    
  return includeCurrencySymbol ? `€${formattedValue}` : formattedValue;
}

/**
 * Safely formats a date string, handling invalid or null dates
 * @param dateString The date string to format
 * @param fallback The fallback value if date is invalid (default: "N/A")
 * @returns Formatted date or fallback string
 */
export function safeFormatDate(dateString: string | null | undefined, fallback: string = "N/A"): string {
  if (!dateString) return fallback;
  
  try {
    // Handle different date formats
    let date: Date | null = null;
    
    // First try our safe parser that handles various formats
    date = safeParseDate(dateString);
    
    // If that doesn't work, try direct parsing
    if (!date) {
      // Don't try to create a date directly if it could be invalid
      // This was causing the "Invalid time value" error
      return fallback;
    }
    
    return date.toLocaleDateString();
  } catch (e) {
    console.error("Error formatting date:", e);
    return fallback;
  }
}

// Enhanced version of safeFormatDate that tries multiple date fields and formats
export function extractAndFormatDate(tenderData: any, fallback: string = "N/A"): string {
  if (!tenderData) return fallback;
  
  // List of common date field paths to check
  const dateFields = [
    // Direct fields
    "deadline-receipt-request.0",
    "deadline-receipt-tender-date-lot.0",
    "deadline_date",
    "deadline",
    
    // Nested in tender_data
    "tender_data.deadline-receipt-tender-date-lot.0", 
    "tender_data.deadline_date",
    "tender_data.deadline",
    
    // Nested in match_reasons
    "match_reasons.deadline-receipt-tender-date-lot.0",
    "tender_data.match_reasons.deadline-receipt-tender-date-lot.0"
  ];
  
  // Try each field path
  for (const fieldPath of dateFields) {
    const value = getNestedValue(tenderData, fieldPath);
    if (value) {
      const formatted = safeFormatDate(value);
      if (formatted !== fallback) {
        return formatted;
      }
    }
  }
  
  return fallback;
}

// Helper function to safely access nested object properties using a path string
function getNestedValue(obj: any, path: string): any {
  if (!obj) return undefined;
  
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    
    // Handle array index access
    if (/^\d+$/.test(part) && Array.isArray(current)) {
      current = current[parseInt(part, 10)];
    } else {
      current = current[part];
    }
  }
  
  return current;
}

// Re-export the date validation functions that are used in this file
export { safeParseDate } from "@/utils/dateValidation";

/**
 * Format a number with commas as thousands separators
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat().format(num);
}

/**
 * Convert a character count to approximate pages (rough estimate)
 */
export function charsToPages(chars: number): number {
  // Using 2000 characters per page as a rough estimate
  return Math.round(chars / 2000);
}

/**
 * Convert a token count to approximate pages (rough estimate)
 */
export function tokensToPages(tokens: number): number {
  // Using 1000 tokens per page as a rough estimate
  return Math.round(tokens / 1000);
}
